import React from 'react';
import { Calendar, User, ArrowRight, Tag } from 'lucide-react';

const Blog = () => {
  const blogs = [
    {
      id: 1,
      title: "The Rise of Agentic AI in Financial Services: From Automation to Autonomy",
      excerpt: "This article perfectly validates why the industry is moving from 'passive' tools that just summarize data to 'agentic' systems that can plan, execute, and adapt complex workflows without constant human hand-holding.",
      author: "Moody's Analytics",
      date: "2025",
      tag: "AI",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=2070",
      url: "https://www.moodys.com/web/en/us/creditview/blog/agentic-ai-in-financial-services.html"
    },
    {
      id: 2,
      title: "State of the Post-Quantum Internet in 2025",
      excerpt: "A 'must-read' for developers and CTOs. It provides a realistic, non-alarmist look at the 'Harvest Now, Decrypt Later' threat and details exactly how the internet's infrastructure is quietly being upgraded to PQC standards right now.",
      author: "Cloudflare",
      date: "2025",
      tag: "Cybersecurity",
      image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&q=80&w=2070",
      url: "https://blog.cloudflare.com/pq-2025/"
    },
    {
      id: 3,
      title: "Finance Digital Transformation: Predictions for 2025",
      excerpt: "This piece paints a picture of the 'Finance Factory' of the future—where transactions are touchless and finance teams focus entirely on insights. It sets the stage for why tools like LendSmart AI are necessary for survival, not just efficiency.",
      author: "Deloitte",
      date: "2025",
      tag: "Finance",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=2070",
      url: "https://www.deloitte.com/us/en/what-we-do/capabilities/finance-transformation/articles/finance-digital-transformation-for-cfos.html"
    },
    {
      id: 4,
      title: "Crypto-Agility and Quantum-Safe Readiness",
      excerpt: "This article introduces the concept of 'Crypto-Agility'—the ability to switch security standards quickly as threats evolve. It positions PQC migration not as a one-time fix, but as a permanent strategic advantage for the enterprise.",
      author: "IBM Quantum",
      date: "2025",
      tag: "Cybersecurity",
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=2070",
      url: "https://www.ibm.com/quantum/blog/crypto-agility"
    },
    {
      id: 5,
      title: "The 2025 McKinsey Global Payments Report",
      excerpt: "A deep dive into how 'programmable compliance' and embedded intelligence will define the next decade of payments. It supports the argument that compliance rules must be woven into the code itself, rather than checked manually later.",
      author: "McKinsey & Company",
      date: "2025",
      tag: "Finance",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=2070",
      url: "https://www.mckinsey.com/industries/financial-services/our-insights/global-payments-report"
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 to-blue-900 py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Ordinaa Insights</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Expert analysis and trends in Artificial Intelligence, Cyber Security, and Financial Technology.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.map((blog) => (
              <article key={blog.id} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group tap-highlight active:scale-[0.98]">
                <a href={blog.url} target="_blank" rel="noopener noreferrer" className="block">
                  <div className="relative h-36 sm:h-48 overflow-hidden">
                    <img
                      src={blog.image}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-gray-800 flex items-center gap-1">
                      <Tag size={12} />
                      {blog.tag}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                      <span className="flex items-center gap-1"><User size={12} /> {blog.author}</span>
                      <span className="flex items-center gap-1"><Calendar size={12} /> {blog.date}</span>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 group-hover:text-cyan-600 transition-colors line-clamp-2">
                      {blog.title}
                    </h3>
                    <p className="text-gray-600 mb-6 text-sm leading-relaxed line-clamp-3">
                      {blog.excerpt}
                    </p>

                    <button className="text-cyan-600 font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all">
                      Read Article <ArrowRight size={16} />
                    </button>
                  </div>
                </a>
              </article>
            ))}
          </div>

          <div className="mt-16 text-center">
            <button className="px-8 py-3 border border-gray-300 rounded-full font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
              Load More Articles
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Stay Ahead of the Curve</h2>
          <p className="text-gray-600 mb-8">
            Get the latest insights on AI and Security delivered directly to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="Enter your email" 
              className="flex-1 px-6 py-3 rounded-full border border-gray-300 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
            />
            <button className="px-8 py-3 bg-gray-900 text-white rounded-full font-semibold hover:bg-gray-800 transition-colors shadow-lg">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
